import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Lightbulb, UserCheck, Heart, Rocket, ArrowRight, CheckCircle2, X, Mail, User, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { featuredBuyers } from '../mock';
import { submitBuyerApplication } from '../services/api';

const Buyers = () => {
  const navigate = useNavigate();
  
  // Buyer Join Form State
  const [showBuyerForm, setShowBuyerForm] = useState(false);
  const [buyerFormStep, setBuyerFormStep] = useState(1);
  const [buyerFormData, setBuyerFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    buyer_type: ''
  });
  const [buyerFormLoading, setBuyerFormLoading] = useState(false);
  const [buyerFormSuccess, setBuyerFormSuccess] = useState(false);
  const [buyerFormError, setBuyerFormError] = useState('');

  const buyerTypes = [
    'Cybersecurity',
    'Developer Tools', 
    'Finance Tech',
    'HR Tech',
    'Other'
  ];

  // Form Handlers
  const handleBuyerEmailSubmit = (e) => {
    e.preventDefault();
    if (!buyerFormData.email || !buyerFormData.email.includes('@')) {
      setBuyerFormError('Please enter a valid work email');
      return;
    }
    setBuyerFormError('');
    setBuyerFormStep(2);
  };

  const handleBuyerFormSubmit = async (e) => {
    e.preventDefault();
    if (!buyerFormData.first_name || !buyerFormData.last_name || !buyerFormData.buyer_type) {
      setBuyerFormError('Please fill in all fields');
      return;
    }
    
    setBuyerFormLoading(true);
    setBuyerFormError('');
    
    try {
      await submitBuyerApplication(buyerFormData);
      setBuyerFormSuccess(true);
    } catch (error) {
      setBuyerFormError('Something went wrong. Please try again.');
    } finally {
      setBuyerFormLoading(false);
    }
  };

  const resetBuyerForm = () => {
    setShowBuyerForm(false);
    setBuyerFormStep(1);
    setBuyerFormData({ email: '', first_name: '', last_name: '', buyer_type: '' });
    setBuyerFormSuccess(false);
    setBuyerFormError('');
  };

  const benefits = [
    {
      icon: Lightbulb,
      title: 'Thought Leadership',
      description: 'Elevate your personal brand. Gain visibility in your industry and accelerate your career growth.',
      weight: 50,
      color: 'blue'
    },
    {
      icon: UserCheck,
      title: 'Advisor Opportunities',
      description: 'Find promising startups to advise. Many judges become advisors to the companies they meet.',
      weight: 33,
      color: 'cyan'
    },
    {
      icon: Users,
      title: 'Peer Networking',
      description: 'Connect with senior leaders—CISOs, VPs, and Directors—from top companies.',
      weight: 17,
      color: 'purple'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <a href="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-white">The Founder Initiative</span>
          </a>
          <nav className="hidden md:flex gap-8 text-sm font-medium text-slate-400">
            <a href="/buyers" className="text-white transition-colors">Buyers</a>
            <a href="/podcast" className="hover:text-white transition-colors">Podcast</a>
            <a href="/sponsors" className="hover:text-white transition-colors">Sponsors</a>
          </nav>
          <a 
            href="https://app.thefounderinitiative.com" 
            className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0 h-10 px-4 py-2"
          >
            Login
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-full px-4 py-2">
              <Users className="w-4 h-4 text-green-400" />
              <span className="text-sm text-green-400 font-medium">Become a Judge</span>
            </div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Shape the Future of{' '}
            <span className="bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent">
              Startups
            </span>
          </h1>
          
          <p className="text-xl text-slate-400 leading-relaxed max-w-3xl mx-auto mb-8">
            Join an exclusive group of enterprise leaders who evaluate innovative startups, 
            provide actionable feedback, and help founders find product-market fit.
          </p>

          <Button 
            onClick={() => setShowBuyerForm(true)}
            size="lg"
            className="bg-gradient-to-r from-green-500 to-cyan-500 hover:from-green-600 hover:to-cyan-600 text-white border-0 text-lg px-8 py-6"
          >
            Apply to be a Judge <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Give Back Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-green-500/5 to-cyan-500/5 border-y border-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-green-500/20 to-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6 ring-1 ring-green-500/30">
            <Heart className="w-8 h-8 text-green-400" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">
            Give Back to the Founder Ecosystem
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Every great company started with founders who needed guidance. By sharing your expertise, 
            you're helping the next generation of entrepreneurs build solutions that matter.
          </p>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Why Judges Join</h2>
            <p className="text-lg text-slate-400">Here's what our judges value most about participating</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="bg-slate-900/50 border-slate-800 relative overflow-hidden">
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${
                  benefit.color === 'blue' ? 'from-blue-500 to-blue-400' :
                  benefit.color === 'cyan' ? 'from-cyan-500 to-cyan-400' :
                  'from-purple-500 to-purple-400'
                }`} />
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      benefit.color === 'blue' ? 'bg-blue-500/10' :
                      benefit.color === 'cyan' ? 'bg-cyan-500/10' :
                      'bg-purple-500/10'
                    }`}>
                      <benefit.icon className={`w-6 h-6 ${
                        benefit.color === 'blue' ? 'text-blue-400' :
                        benefit.color === 'cyan' ? 'text-cyan-400' :
                        'text-purple-400'
                      }`} />
                    </div>
                    <div className={`text-2xl font-bold ${
                      benefit.color === 'blue' ? 'text-blue-400' :
                      benefit.color === 'cyan' ? 'text-cyan-400' :
                      'text-purple-400'
                    }`}>
                      {benefit.weight}%
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Past Judges Section */}
      <section className="py-16 px-6 bg-slate-900/30 border-y border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Decision Makers Who've Joined</h2>
            <p className="text-lg text-slate-400">Senior leaders from top companies who've helped shape our startups</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredBuyers.map((buyer) => (
              <Card key={buyer.id} className="bg-slate-900/80 border-slate-800 hover:border-green-500/30 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <img 
                      src={buyer.image} 
                      alt={buyer.name}
                      className="w-16 h-16 rounded-full bg-slate-800 ring-2 ring-slate-700 object-cover"
                    />
                    <div className="flex-1">
                      <h3 className="font-bold text-white text-lg mb-1">{buyer.name}</h3>
                      <p className="text-green-400 font-semibold text-sm mb-1">{buyer.title}</p>
                      <p className="text-slate-300 font-medium mb-2">{buyer.company}</p>
                      <Badge variant="secondary" className="text-xs bg-slate-800 text-slate-300 border-slate-700">
                        {buyer.category}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + Form Section */}
      <section className="py-20 px-6">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Make an Impact?
            </h2>
            <p className="text-lg text-slate-400">
              Join our network of judges and help founders build the next generation of enterprise software.
            </p>
          </div>
          
          {/* Join Form */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-8">
              {buyerFormSuccess ? (
                /* Success State */
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="font-bold text-white text-2xl mb-2">Application Submitted!</h3>
                  <p className="text-slate-400 mb-6">Thank you for your interest. We'll be in touch soon with next steps.</p>
                  <Button 
                    variant="ghost" 
                    onClick={resetBuyerForm}
                    className="text-slate-400 hover:text-white"
                  >
                    Submit Another Application
                  </Button>
                </div>
              ) : buyerFormStep === 1 ? (
                /* Step 1: Email */
                <form onSubmit={handleBuyerEmailSubmit} className="space-y-6">
                  <div className="text-center mb-4">
                    <h3 className="font-bold text-white text-xl mb-2">Apply to be a Judge</h3>
                    <p className="text-slate-400">Enter your work email to get started</p>
                  </div>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      data-testid="buyer-email-input"
                      placeholder="you@company.com"
                      value={buyerFormData.email}
                      onChange={(e) => setBuyerFormData({...buyerFormData, email: e.target.value})}
                      className="w-full pl-12 pr-4 py-4 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                    />
                  </div>
                  {buyerFormError && <p className="text-red-400 text-sm">{buyerFormError}</p>}
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-green-500 to-cyan-500 hover:from-green-600 hover:to-cyan-600 text-white py-4 text-lg"
                  >
                    Continue <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </form>
              ) : (
                /* Step 2: Details */
                <form onSubmit={handleBuyerFormSubmit} className="space-y-5">
                  <div className="text-center mb-4">
                    <h3 className="font-bold text-white text-xl mb-1">Almost there!</h3>
                    <p className="text-slate-400 text-sm">{buyerFormData.email}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        data-testid="buyer-firstname-input"
                        placeholder="First Name"
                        value={buyerFormData.first_name}
                        onChange={(e) => setBuyerFormData({...buyerFormData, first_name: e.target.value})}
                        className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        data-testid="buyer-lastname-input"
                        placeholder="Last Name"
                        value={buyerFormData.last_name}
                        onChange={(e) => setBuyerFormData({...buyerFormData, last_name: e.target.value})}
                        className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-3 flex items-center gap-2">
                      <Briefcase className="w-4 h-4" /> What area do you specialize in?
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {buyerTypes.map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setBuyerFormData({...buyerFormData, buyer_type: type})}
                          className={`px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                            buyerFormData.buyer_type === type
                              ? 'bg-green-500 text-white'
                              : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                  {buyerFormError && <p className="text-red-400 text-sm">{buyerFormError}</p>}
                  <div className="flex gap-3 pt-2">
                    <Button 
                      type="button"
                      variant="ghost"
                      onClick={() => setBuyerFormStep(1)}
                      className="text-slate-400 hover:text-white"
                    >
                      Back
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={buyerFormLoading}
                      className="flex-1 bg-gradient-to-r from-green-500 to-cyan-500 hover:from-green-600 hover:to-cyan-600 text-white"
                    >
                      {buyerFormLoading ? 'Submitting...' : 'Submit Application'}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-800">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} The Founder Initiative. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Floating Form Modal (for hero CTA) */}
      {showBuyerForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-md">
            <button 
              onClick={() => setShowBuyerForm(false)}
              className="absolute -top-12 right-0 p-2 text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-8">
                {buyerFormSuccess ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-400" />
                    </div>
                    <h3 className="font-bold text-white text-2xl mb-2">Application Submitted!</h3>
                    <p className="text-slate-400 mb-6">We'll be in touch soon.</p>
                    <Button 
                      onClick={() => { resetBuyerForm(); setShowBuyerForm(false); }}
                      className="bg-gradient-to-r from-green-500 to-cyan-500 hover:from-green-600 hover:to-cyan-600 text-white"
                    >
                      Done
                    </Button>
                  </div>
                ) : buyerFormStep === 1 ? (
                  <form onSubmit={handleBuyerEmailSubmit} className="space-y-6">
                    <div className="text-center mb-4">
                      <h3 className="font-bold text-white text-xl mb-2">Apply to be a Judge</h3>
                      <p className="text-slate-400">Enter your work email to get started</p>
                    </div>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="email"
                        placeholder="you@company.com"
                        value={buyerFormData.email}
                        onChange={(e) => setBuyerFormData({...buyerFormData, email: e.target.value})}
                        className="w-full pl-12 pr-4 py-4 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
                      />
                    </div>
                    {buyerFormError && <p className="text-red-400 text-sm">{buyerFormError}</p>}
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-green-500 to-cyan-500 hover:from-green-600 hover:to-cyan-600 text-white py-4 text-lg"
                    >
                      Continue <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleBuyerFormSubmit} className="space-y-5">
                    <div className="text-center mb-4">
                      <h3 className="font-bold text-white text-xl mb-1">Almost there!</h3>
                      <p className="text-slate-400 text-sm">{buyerFormData.email}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          placeholder="First Name"
                          value={buyerFormData.first_name}
                          onChange={(e) => setBuyerFormData({...buyerFormData, first_name: e.target.value})}
                          className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Last Name"
                          value={buyerFormData.last_name}
                          onChange={(e) => setBuyerFormData({...buyerFormData, last_name: e.target.value})}
                          className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-3 flex items-center gap-2">
                        <Briefcase className="w-4 h-4" /> What area do you specialize in?
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {buyerTypes.map((type) => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => setBuyerFormData({...buyerFormData, buyer_type: type})}
                            className={`px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                              buyerFormData.buyer_type === type
                                ? 'bg-green-500 text-white'
                                : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
                            }`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    </div>
                    {buyerFormError && <p className="text-red-400 text-sm">{buyerFormError}</p>}
                    <div className="flex gap-3 pt-2">
                      <Button 
                        type="button"
                        variant="ghost"
                        onClick={() => setBuyerFormStep(1)}
                        className="text-slate-400 hover:text-white"
                      >
                        Back
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={buyerFormLoading}
                        className="flex-1 bg-gradient-to-r from-green-500 to-cyan-500 hover:from-green-600 hover:to-cyan-600 text-white"
                      >
                        {buyerFormLoading ? 'Submitting...' : 'Submit Application'}
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default Buyers;
